import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BrandWordmark from "@/components/marketing/BrandWordmark";
import { ArrowRight, Check } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

interface LandingPricingProps {
  sectionId?: string;
  eyebrow?: string;
  title: ReactNode;
  description: ReactNode;
  theme?: "light" | "dark";
}

const premiumTiers = [
  {
    name: "1 Week",
    price: "$4.99",
    compareAtPrice: "$7.99",
    note: "One-time trial",
    bullets: [] as string[],
    cta: "Test it out",
  },
  {
    name: "1 Month",
    price: "$24.99",
    note: "Lock in for your hunt",
    bullets: [] as string[],
    cta: "Let's do this",
    featured: true,
  },
  {
    name: "3 Months",
    price: "$49.99",
    note: "Best value",
    bullets: [] as string[],
    cta: "3 months - 1 free",
  },
] as const;

export function LandingPricing({ sectionId, eyebrow, title, description, theme = "light" }: LandingPricingProps) {
  const isDark = theme === "dark";

  return (
    <section
      id={sectionId}
      className={isDark ? "bg-[#223149] py-20 text-white" : "bg-[#f4f8fc] py-20 text-slate-900"}
    >
      <div className="container mx-auto px-6">
        <div className="mx-auto max-w-3xl text-center">
          {eyebrow && (
            <p className={`mb-4 text-sm font-semibold uppercase tracking-[0.28em] ${isDark ? "text-sky-200" : "text-sky-700"}`}>
              {eyebrow}
            </p>
          )}
          <h2 className="text-3xl font-bold md:text-4xl">{title}</h2>
          <p className={`mt-4 text-base md:text-lg ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {description}
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <Card className={`rounded-[2rem] border shadow-lg ${isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}>
            <CardContent className="p-8">
              <p className={`text-sm font-semibold uppercase tracking-[0.24em] ${isDark ? "text-sky-200" : "text-sky-700"}`}>
                Free
              </p>
              <div className="mt-4 flex items-end gap-3">
                <h3 className={`text-5xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>$0</h3>
                <p className={`pb-2 text-sm ${isDark ? "text-slate-300" : "text-slate-500"}`}>stay organized</p>
              </div>
              <p className={`mt-5 text-sm leading-7 ${isDark ? "text-slate-200" : "text-slate-600"}`}>
                Organize your job hunt with{" "}
                <BrandWordmark
                  hiredClassName="font-bold text-[#4f9ecb]"
                  coachClassName="font-bold text-[#334763]"
                />{" "}
                for free. Try out the premium features free once a week.
              </p>
              <div className="mt-8 flex justify-center">
                <Button
                  asChild
                  className={`h-16 rounded-full px-12 text-lg font-semibold md:h-20 md:px-16 md:text-xl ${isDark ? "border-2 border-white/40 bg-transparent text-white hover:bg-white/10" : "border-2 border-slate-300 bg-white text-slate-900 hover:bg-slate-50"}`}
                >
                  <Link to="/register" className="inline-flex items-center justify-center gap-3">
                    Start Free
                    <ArrowRight className="h-5 w-5 shrink-0 md:h-6 md:w-6" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="rounded-[2rem] border border-amber-300/50 bg-gradient-to-br from-amber-50 via-white to-white p-6 shadow-xl shadow-amber-100">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-amber-700">Premium</p>
                <h3 className="mt-2 text-3xl font-bold text-slate-900">All features unlocked</h3>
              </div>
              <p className="text-sm text-slate-500">No auto-renew nonsense</p>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-3 md:items-stretch">
              {premiumTiers.map((tier) => {
                const featured = "featured" in tier && tier.featured;

                return (
                  <div
                    key={tier.name}
                    className={`relative flex h-full min-h-[280px] flex-col rounded-[1.75rem] border p-5 pt-6 ${
                      featured
                        ? "z-[1] border-2 border-amber-400 bg-gradient-to-b from-amber-100 via-amber-50/90 to-white shadow-xl shadow-amber-200/60 ring-2 ring-amber-300/40 md:py-6"
                        : "border-slate-200 bg-white shadow-sm"
                    }`}
                  >
                    {featured && (
                      <span className="absolute -top-3 left-1/2 z-[2] -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-md">
                        Most popular
                      </span>
                    )}
                    <div className="flex min-h-0 flex-1 flex-col">
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                        {tier.name}
                      </p>
                      <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <h4
                          className={`font-bold tracking-tight text-slate-900 ${
                            featured ? "text-5xl md:text-[2.75rem]" : "text-4xl"
                          }`}
                        >
                          {tier.price}
                        </h4>
                        {"compareAtPrice" in tier && tier.compareAtPrice && (
                          <span className="text-lg font-medium text-slate-400 line-through decoration-slate-400 decoration-2">
                            {tier.compareAtPrice}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 min-h-[2.5rem] text-sm leading-snug text-slate-600">{tier.note}</p>
                      {tier.bullets.length > 0 && (
                        <ul className="mt-4 space-y-3 text-sm text-slate-700">
                          {tier.bullets.map((bullet) => (
                            <li key={bullet} className="flex gap-3">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-auto pt-6">
                        <Button
                          asChild
                          className={`h-12 w-full rounded-full text-sm font-semibold ${
                            featured
                              ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md hover:from-amber-600 hover:to-orange-600"
                              : "bg-sky-600 text-white hover:bg-sky-500"
                          }`}
                        >
                          <Link to="/register" className="inline-flex items-center justify-center gap-2">
                            {tier.cta}
                            <ArrowRight className="h-4 w-4 shrink-0" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LandingPricing;
